import { formatCompactCurrency, formatPercent } from '../../lib/format.js';
import { currencyOf, metricValueOf, type ResultRow } from './resultRows.js';

const SPEND_METRIC = 'Spend';
const FATIGUE = 'fatigue';
const CTR_RATIO = 'ctr_ratio';
const RECENT_SPEND = 'recent_spend';
const ACTIVE_DAYS = 'active_days';
const REFRESH_COUNT = 3;
const DAYS_PER_WEEK = 7;

export interface ReportTile {
  value: string;
  label: string;
  negative?: boolean;
}

export interface ReportRecommendation {
  title: string;
  detail: string;
}

export interface ReportSummary {
  headline: string;
  detail: string | null;
  section: string;
  tiles: ReportTile[];
  recommendation: ReportRecommendation | null;
}

function ratioOf(row: ResultRow, key: string): number | null {
  const block = row.values[FATIGUE];
  if (typeof block !== 'object' || block === null) return null;
  const value = (block as Record<string, unknown>)[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function spendOf(row: ResultRow): number {
  return metricValueOf(row, SPEND_METRIC) ?? 0;
}

function weeklyExposure(rows: ResultRow[]): number | null {
  const rates = rows.flatMap((row) => {
    const spend = ratioOf(row, RECENT_SPEND);
    const days = ratioOf(row, ACTIVE_DAYS);
    return spend === null || days === null || days <= 0 ? [] : [(spend / days) * DAYS_PER_WEEK];
  });
  if (rates.length === 0) return null;
  return rates.reduce((total, rate) => total + rate, 0);
}

function meanDrop(rows: ResultRow[]): number | null {
  const ratios = rows.flatMap((row) => {
    const ratio = ratioOf(row, CTR_RATIO);
    return ratio === null ? [] : [ratio - 1];
  });
  if (ratios.length === 0) return null;
  return ratios.reduce((total, drop) => total + drop, 0) / ratios.length;
}

function refreshTheWorst(rows: ResultRow[], symbol: string): ReportRecommendation | null {
  const total = rows.reduce((sum, row) => sum + spendOf(row), 0);
  if (rows.length <= REFRESH_COUNT || total === 0) return null;

  const worst = [...rows]
    .sort((left, right) => spendOf(right) - spendOf(left))
    .slice(0, REFRESH_COUNT);
  const share = worst.reduce((sum, row) => sum + spendOf(row), 0) / total;

  return {
    title: `Refresh the ${REFRESH_COUNT} highest-spend creatives`,
    detail: `They account for ${formatPercent(share, 0)} of exposed budget (${formatCompactCurrency(
      total,
      symbol,
    )} in total). Use Iteration Recommendations for replacements.`,
  };
}

export function fatigueReport(rows: ResultRow[]): ReportSummary {
  const symbol = rows.length === 0 ? '$' : currencyOf(rows[0]?.values ?? {});
  const total = rows.reduce((sum, row) => sum + spendOf(row), 0);
  const drop = meanDrop(rows);

  const weekly = weeklyExposure(rows);

  return {
    headline: `${rows.length} ${rows.length === 1 ? 'fatigued creative' : 'fatigued creatives'} flagged`,
    detail:
      weekly === null
        ? null
        : `About ${formatCompactCurrency(weekly, symbol)}/wk of spend exposed to rising CPM and CTR decay.`,
    section: 'Flagged creatives',
    tiles: [
      { value: formatCompactCurrency(total, symbol), label: 'Total spend in risk' },
      { value: String(rows.length), label: 'Creatives flagged' },
      ...(drop === null
        ? []
        : [{ value: formatPercent(drop, 0), label: 'Avg. CTR drop', negative: drop < 0 }]),
    ],
    recommendation: refreshTheWorst(rows, symbol),
  };
}

export function creativeReport(rows: ResultRow[]): ReportSummary {
  const symbol = rows.length === 0 ? '$' : currencyOf(rows[0]?.values ?? {});
  const total = rows.reduce((sum, row) => sum + spendOf(row), 0);

  return {
    headline: `${rows.length} ${rows.length === 1 ? 'creative' : 'creatives'}`,
    detail: null,
    section: rows.length === 1 ? 'Creative' : 'Creatives',
    tiles: [
      { value: String(rows.length), label: 'Creatives' },
      { value: formatCompactCurrency(total, symbol), label: 'Total spend' },
    ],
    recommendation: null,
  };
}

export function reportSummaryOf(rows: ResultRow[]): ReportSummary {
  return rows.some((row) => ratioOf(row, CTR_RATIO) !== null)
    ? fatigueReport(rows)
    : creativeReport(rows);
}
